import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Loading } from "../components/Loading";
import api from "../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import { i18n } from "../localization/i18n";
import { services } from "./authService";
import { ISignature, useOnboard } from "./OnboardContext";
import { roles } from "../utils/constants";
import axios from "axios";
import * as Bowser from "bowser"
import { useFinancialStatus } from "./FinancialStatusContext";
import { requestFirebaseNotificationPermission } from "../services/firebase";
import { IPlan } from "../modules/Admin/pages/Plans/dtos";

interface ISignInCredentials {
  email: string;
  password: string;
}

interface IValidateCode extends ISignInCredentials {
  registration_token: string;
  baseApiUrl: string;
}

interface Licensee {
  language: string;
  id: string;
  contract: string;
  user_term: ITermosUso;
  is_blocked: boolean;
  signature_status?: "active" | "blocked" | "cancelled";
  active_signature?: ISignature;
  completed_onboard: boolean;
  situation: string;
  country: string;
  signature: ISignature[];
  state: string;
  phone: string;
  name: string;
  companyName: string;
  manager_name: string;
  document_number: string;
  printers: IPrinter[];
  is_fixpoint: boolean;
  bills: Bill[];
  provider: Licensee;
  locator?: {
    id?: string;
    name?: string;
    phone?: string;
    is_public?: boolean;
    use_billing_contact?: boolean;
    picture_link?: string;
  };
}

export interface Bill {
  id: string;
  type: string;
  bill_value: string;
  referring_period: string;
  due_date: Date;
  payment_date?: Date;
  is_recurrence: boolean;
  licensee_id: string;
  card_id?: string;
  address_id?: string;
  payment_method: string;
  currency: string;
  paid_ammount: string;
  payment_situation:
  | "cancelado"
  | "aguardando"
  | "pago"
  | "vencido"
  | "dispute"
  | "estorno_parcial"
  | "estorno"
  | "chargeback"
  | "credito"
  | string;
  transaction_code: string;
  licensee: Licensee;
  payment_link: string;
}
interface IPrinter {
  id: string;
  name: string;
}

interface Person {
  id: string;
  name: string;
  licensee: Licensee;
  cpf: string;
  country: string;
  state: string;
  city: string;
  cellphone: string;
  expertise: string;
  professional_number: string;
  termsAccepted: boolean;
}

interface IUserData {
  name: string;
  email: string;
  role: string;
  id: string;
  person: Person;
  profile_picture_link: string;
}

export interface ITermosUso {
  id: string;
  name: string;
  version: string;
  document_link: string;
}

interface IAuthContextState {
  user: IUserData;
  reloadUser: () => Promise<void>;
  signIn(credentials: ISignInCredentials): Promise<any>;
  signOut(): void;
  validateCode(validation: IValidateCode): Promise<IUserData>;
  navigationAction?: INavigationAction;
  setNavigationAction: Dispatch<SetStateAction<INavigationAction | undefined>>;
  licenseeBills: Bill[];
  licenseeFixcoins: number;
  currency: ICurrencyInfos | undefined;
  activePlan?: IPlan;
  isFixiter: boolean;
  isLicenseeWithPrinter: boolean;
  hasProvider: boolean;
  canPlaceOrders: boolean;
}

interface INavigationAction {
  hasButton: boolean;
  description: string;
  action?: string;
  link?: string;
  functionToBeExecuted?: () => void;
  customElement?: React.ReactNode;
}

interface IAuthState {
  loading: boolean;
  token: string;
  user: IUserData;
  refresh_token: string;
}

export interface ICurrencyInfos {
  code: string;
  country: string;
  name: string;
  symbol: string;
  custom_tax: number;
  urgency_tax: number;
  is_default: boolean;
  fixcoin_exchange: number;
}

export interface IUserDeviceInfos {
  id?: string;
  ip: string;
  city: string;
  state: string;
  country: string;
  latitude: string;
  longitude: string;
  timezone: string;
  user_agent: string;
  screen_resolution: string;
  allowed_to_print: boolean;
}

const AuthContext = createContext<IAuthContextState>({} as IAuthContextState);

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState<IAuthState>({ loading: true } as IAuthState);
  const [navigationAction, setNavigationAction] = useState<INavigationAction>();
  const [licenseeBills, setLicenseeBills] = useState<Bill[]>([]);
  const [licenseeFixcoins, setLicenseeFixcoins] = useState<number>(0);
  const [currency, setCurrency] = useState<ICurrencyInfos>({
    code: "BRL",
    country: "BR",
    name: "Real",
    symbol: "R$",
    custom_tax: 30,
    urgency_tax: 30,
    is_default: true,
    fixcoin_exchange: 1,
  });
  const { planHasExpired } = useOnboard();
  const { isOverdue } = useFinancialStatus();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const bowser = Bowser.getParser(navigator.userAgent)
  const deviceID = localStorage.getItem("@FixitApp:Device")

  const isOpenRoute = pathname.startsWith('/fixiter/course') || 
                      pathname.startsWith('/registration') || 
                      pathname.startsWith('/find') || 
                      pathname.startsWith('/docs');

  // useEffect(() => {
  //   if(data?.user?.id) {
  //     navigate('')
  //   }
  // }, [data?.user?.id])

  const enrichUserWithLicensee = async (baseUser: any) => {
    if (!baseUser) return baseUser;
    if (
      baseUser.role === roles.licensee ||
      baseUser.role === roles.fixiter
    ) {
      try {
        const licRes = await api.get("/licensees/user/onboard");
        if (licRes.data) {
          return {
            ...baseUser,
            licensee: licRes.data,
            person: {
              ...(baseUser.person || {}),
              licensee: licRes.data,
            },
          };
        }
      } catch (e) {
        console.error("Failed to fetch licensee details for user:", e);
      }
    }
    return baseUser;
  };

  const updateUserCurrencyInfo = useCallback((userData?: IUserData) => {
    const userToProcess = userData || data.user;
    if (!userToProcess) return;

    const userPlan =
      (userToProcess as any)?.person?.licensee?.signature?.[0]?.plan ||
      (userToProcess as any)?.licensee?.signature?.[0]?.plan ||
      (userToProcess as any)?.signature?.plan ||
      (userToProcess as any)?.signature?.[0]?.plan;

    const planCurrencyRaw = userPlan?.currency;
    const planCurrencyCode =
      typeof planCurrencyRaw === "string"
        ? planCurrencyRaw
        : (planCurrencyRaw as any)?.value || "BRL";

    setCurrency({
      code: planCurrencyCode || "BRL",
      country: userToProcess.person?.country || "BR",
      name: planCurrencyCode === "USD" ? "US Dollar" : "Real",
      symbol: planCurrencyCode === "USD" ? "$" : "R$",
      custom_tax: Number(userPlan?.custom_tax ?? 30),
      urgency_tax: Number(userPlan?.urgency_tax ?? 30),
      is_default: true,
      fixcoin_exchange: 1,
    });
  }, [data.user]);

  const verifyUser = (token: any, refresh_token: any) => {
    api
      .get("/users/me")
      .then(async (response) => {
        const user = response.data;
        if (!user || !user.id) {
          signOut();
          return;
        }
        const enrichedUser = await enrichUserWithLicensee(user);
        setData((prevState) => ({
          ...prevState,
          token,
          user: enrichedUser,
          refresh_token,
          loading: false,
        }));
        updateUserCurrencyInfo(enrichedUser);
        !deviceID && getAndRegisterUserDevice();
      })
      .catch((err) => {
        signOut();
      });
  };

  const reloadUser = async () => {
    try {
      const response = await api.get("/users/me");
      const user = response.data;
      if (!user || !user.id) {
        signOut();
        return;
      }
      const enrichedUser = await enrichUserWithLicensee(user);
      setData((prevState) => ({ ...prevState, user: enrichedUser, loading: false }));
      updateUserCurrencyInfo(enrichedUser);
    } catch (err) { }
  };

  const activePlan = useMemo((): IPlan | undefined => {
    const userToProcess = data?.user;
    if (!userToProcess) return undefined;
    const isSigActive = (s: any) =>
      Boolean(s) &&
      (!s.end_date || new Date(s.end_date).getTime() >= Date.now()) &&
      s.status !== "cancelled" &&
      s.status !== "canceled";
    const targetLic = (userToProcess as any)?.person?.licensee || (userToProcess as any)?.licensee;
    if (
      isSigActive(targetLic?.active_signature) &&
      (targetLic?.active_signature?.plan || targetLic?.active_signature?.plans)
    ) {
      return targetLic.active_signature.plan || targetLic.active_signature.plans;
    }
    const rawSig =
      targetLic?.signature ||
      targetLic?.signatures ||
      (userToProcess as any)?.signatures;
    const sigList = (Array.isArray(rawSig) ? [...rawSig] : rawSig ? [rawSig] : []).sort((a: any, b: any) => {
      const tA = new Date(a?.created_at || a?.start_date || 0).getTime();
      const tB = new Date(b?.created_at || b?.start_date || 0).getTime();
      return (isNaN(tB) ? 0 : tB) - (isNaN(tA) ? 0 : tA);
    });
    const activeSig =
      sigList.find(isSigActive) ||
      sigList.find((s: any) => s.is_activated) ||
      sigList[0];
    return activeSig?.plan || activeSig?.plans || (userToProcess as any)?.signature?.plan;
  }, [data?.user]);

  const isFixiter = useMemo((): boolean => {
    if (!data?.user) return false;
    if (activePlan && typeof activePlan.has_printer === "boolean") {
      return activePlan.has_printer === false;
    }
    return data.user.role === roles.fixiter;
  }, [data?.user, activePlan]);

  const isLicenseeWithPrinter = useMemo((): boolean => {
    if (!data?.user) return false;
    if (
      data.user.role !== roles.licensee &&
      data.user.role !== roles.fixiter
    ) {
      return false;
    }
    if (activePlan && typeof activePlan.has_printer === "boolean") {
      return activePlan.has_printer === true;
    }
    return data.user.role === roles.licensee;
  }, [data?.user, activePlan]);

  const providerId = useMemo((): string | undefined => {
    return (
      (data?.user as any)?.person?.licensee?.provider_id ||
      (data?.user as any)?.licensee?.provider_id ||
      (data?.user as any)?.person?.licensee?.provider?.id ||
      (data?.user as any)?.licensee?.provider?.id
    );
  }, [data?.user]);

  const hasProvider = isFixiter && !!providerId;
  const canPlaceOrders = isLicenseeWithPrinter || hasProvider;

  useEffect(() => {
    const token = localStorage.getItem("@FixitApp:token");
    const refresh_token = localStorage.getItem("@FixitApp:refresh_token");

    // const user = localStorage.getItem("@FixitApp:user");
    // This code is temporary. Its goal is to remove user data from browser starage.
    /* if (user) {
      localStorage.removeItem("@FixitApp:user");
    } */

    verifyLanguage();

    if (!token || !refresh_token) {
      localStorage.removeItem("@FixitApp:token");
      localStorage.removeItem("@FixitApp:refresh_token");
      setData({ loading: false } as IAuthState);
      return;
    }
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    api
      .get("/users/me")
      .then(async (response) => {
        const user = response.data;
        if (!user || !user.id) {
          signOut();
          return;
        }
        const enrichedUser = await enrichUserWithLicensee(user);
        setData((prevState) => ({
          ...prevState,
          token,
          user: enrichedUser,
          refresh_token,
          loading: false,
        }));
        updateUserCurrencyInfo(enrichedUser);
      })
      .catch((err) => {
        console.log(err);
        signOut();
      });

  }, []);

  async function fetchIP() {
    try {
      const response = await axios.get("https://geolocation-db.com/json/");
      return response.data;
    } catch (error) {
      console.error("Falha em obter endereço IP", error)
    }
  }

  async function getAndRegisterUserDevice() {

    await fetchIP().then(async (data) => {

      const body: IUserDeviceInfos = {
        id: deviceID ?? undefined,
        ip: data.IPv4,
        country: data.country_code,
        city: data.city,
        state: data.state,
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        allowed_to_print: bowser.getPlatformType(true) === 'desktop'
      }

      try {

        await api.post("/user-devices/register", body).then((response) => {
          localStorage.setItem("@FixitApp:Device", response.data.id)
        })
      } catch (error) {
        console.error(error)
      }

    })
  }

  const signIn = useCallback(
    async ({ email, password }: ISignInCredentials) => {
      const response = await api.post("/sessions", {
        email,
        password,
      });

      const { token, user, refresh_token } = response.data;

      localStorage.getItem("@FixitApp:baudRate") === undefined &&
        localStorage.setItem("@FixitApp:baudRate", "115200");

      localStorage.setItem("@FixitApp:token", token);
      localStorage.setItem("@FixitApp:refresh_token", refresh_token);

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const enrichedUser = await enrichUserWithLicensee(user);
      setData({ token, user: enrichedUser, refresh_token, loading: false });
      updateUserCurrencyInfo(enrichedUser);
      verifyUser(token, refresh_token);

      // Firebase Notification Registration
      try {
        const fcmToken = await requestFirebaseNotificationPermission();
        if (fcmToken) {
          await api.post('/notifications/token', {
            fcm_token: fcmToken,
            device_type: 'web'
          });
        }
      } catch (error) {
        console.error("Failed to register notification token:", error);
      }

      const redirectLink = await services.getUserRoleAndRedirect(
        pathname,
        enrichedUser,
        enrichedUser.role,
        planHasExpired,
        isOverdue
      );

      navigate(`${redirectLink}`);
    },
    []
  );

  const validateCode = useCallback(
    async ({ email, password, registration_token, baseApiUrl }: IValidateCode) => {
      const response = await api.post(`/users/create/${registration_token}`, {
        email,
        password,
        baseApiUrl
      });

      const { token, user, refresh_token } = response.data;

      localStorage.setItem("@FixitApp:token", token);
      localStorage.setItem("@FixitApp:refresh_token", refresh_token);

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setData({ token, user, refresh_token, loading: false });

      // Firebase Notification Registration
      try {
        const fcmToken = await requestFirebaseNotificationPermission();
        if (fcmToken) {
          await api.post('/notifications/token', {
            fcm_token: fcmToken,
            device_type: 'web'
          });
        }
      } catch (error) {
        console.error("Failed to register notification token:", error);
      }

      return user;
    },
    []
  );

  const signOut = useCallback(() => {
    localStorage.removeItem("@FixitApp:token");
    localStorage.removeItem("@FixitApp:refresh_token");

    delete api.defaults.headers.common["Authorization"];

    setData({ loading: false } as IAuthState);
    navigate("/");
  }, [navigate]);

  const verifyLanguage = useCallback(() => {
    const languageAlreadySelected = localStorage.getItem("i18nextLng");
    if (languageAlreadySelected) return;

    const browserLanguage = window.navigator.language;
    const spanishLanguage = browserLanguage.startsWith("es");
    const portugueseLanguage = browserLanguage.startsWith("pt");

    if (spanishLanguage) {
      window.localStorage.setItem("i18nextLng", "es");
      i18n.changeLanguage("es");
    } else if (portugueseLanguage) {
      window.localStorage.setItem("i18nextLng", "pt-BR");
      i18n.changeLanguage("pt-BR");
    } else {
      window.localStorage.setItem("i18nextLng", "en");
      i18n.changeLanguage("en");
    }
  }, []);

  async function getLicenseeBills() {
    try {
      await api.get("/bills").then((response) => {
        setLicenseeBills(response.data);
        console.log(response.data)
      });
    } catch (error) {
      console.log("There is no bills for this licensee");
    }
  }

  async function getLicenseeFixcoins(id: string) {
    try {
      await api.get(`/transactions/fixcoin/${id}`).then((response) => {
        setLicenseeFixcoins(response.data);
      });
    } catch (error) {
      console.log("Error to fetch licensee fixcoins");
    }
  }

  useEffect(() => {
    if (data.user) {
      updateUserCurrencyInfo(data.user);
      if (data.user.role === roles.licensee && data.user.person !== null) {
        getLicenseeBills();
        const licenseeId = data.user.person?.licensee?.id;
        if (licenseeId) {
          getLicenseeFixcoins(licenseeId);
        }
      }
    }
  }, [data.user]);

  return (
    <>
      {(!isOpenRoute || !data.loading) && (
        <Loading
          loading={!isOpenRoute ? data.loading : false}
          text={`${i18n.t("login.starting")}`}
          numberOfDots={3}
          dotSymbol="."
        />
      )}
      {/* Incluir componente mockado para exibir ao fundo do Loading*/}
      {(!data.loading || isOpenRoute) && (
        <AuthContext.Provider
          value={{
            user: data.user,
            reloadUser,
            signIn,
            signOut,
            validateCode,
            navigationAction,
            setNavigationAction,
            licenseeBills,
            licenseeFixcoins,
            currency,
            activePlan,
            isFixiter,
            isLicenseeWithPrinter,
            hasProvider,
            canPlaceOrders,
          }}
        >
          {children}
        </AuthContext.Provider>
      )}
    </>
  );
};

function useAuth(): IAuthContextState {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export { AuthProvider, useAuth };
